import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { openLessonPlanPrint } from '../printLessonPlan';
import ChapterPickerModal from '../components/ChapterPickerModal';

const CURRENT_YEAR = new Date().getFullYear();

export default function LessonPlans() {
  const { user } = useAuth();
  const isAdminStaff = user?.role === 'admin' || user?.role === 'staff';
  const [foreignTeachers, setForeignTeachers] = useState([]);
  const [onBehalfTeachid, setOnBehalfTeachid] = useState('');
  const writingAsForeign = isAdminStaff && onBehalfTeachid !== '';
  // 관리자도 일반 교사와 동일하게 자신의 배정 수업에 대한 교수계획서를 작성할 수 있어야 하고,
  // 외부강사(foreign)도 일반 교사와 동일하게 자신이 배정된 수업의 교수계획서를 작성해야 함.
  // staff(행정)는 원외교사를 대신 선택했을 때만 작성 화면이 열림 (원외교사는 플랫폼을 직접 사용할 수 없음)
  const canWritePlan = user?.role === 'teacher' || user?.role === 'admin' || user?.role === 'foreign' || writingAsForeign;
  const isTeacher = user?.role === 'teacher' || user?.role === 'foreign';

  const [year, setYear] = useState(CURRENT_YEAR);
  const [typeclsOptions, setTypeclsOptions] = useState([]);
  const [typecls, setTypecls] = useState('정규');
  const [assignments, setAssignments] = useState([]);
  const [assignmentSections, setAssignmentSections] = useState({}); // { asnid: sections[] }
  const [sectionState, setSectionState] = useState({}); // { crssectno: { checked, thrytime, practime, remark } }
  const [pickerOpen, setPickerOpen] = useState(false);
  const [grade, setGrade] = useState(1);
  const [unitno, setUnitno] = useState(1);
  const [daytime, setDaytime] = useState('');
  const [remark, setRemark] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [teacherNameFilter, setTeacherNameFilter] = useState('');
  const [unitnoFilter, setUnitnoFilter] = useState('');
  const [editingCrsuid, setEditingCrsuid] = useState(null);

  const loadPlans = useCallback(async () => {
    const res = await api.get('/lesson-plans', { params: { year, typecls } });
    setPlans(res.data);
  }, [year, typecls]);

  useEffect(() => {
    if (!canWritePlan) return;
    api.get('/course-plan-types').then((res) => {
      // '잠정'은 스마트폰에서 임시로 작성한 계획서 구분이라 courseplan에는 없지만, PC에서 조회/수정할 수 있어야 한다
      const options = res.data.includes('잠정') ? res.data : [...res.data, '잠정'];
      setTypeclsOptions(options);
      setTypecls((prev) => (options.includes(prev) ? prev : options[0] || prev));
    });
  }, [canWritePlan]);

  useEffect(() => {
    if (!isAdminStaff) return;
    api.get('/admin/teachers', { params: { location: 'external' } }).then((res) => setForeignTeachers(res.data));
  }, [isAdminStaff]);

  useEffect(() => {
    if (canWritePlan) {
      // 수업 배정은 '잠정' 구분이 없으므로 정규수업배정 기준으로 불러온다
      const params = { year, typecls: typecls === '잠정' ? '정규' : typecls };
      if (writingAsForeign) params.teachid = onBehalfTeachid;
      api.get('/teacher/assignments/me', { params }).then(async (res) => {
        setAssignments(res.data);
        const entries = await Promise.all(
          res.data.map((a) =>
            api.get(`/materials/${a.crsmatno}/sections`, { params: { asnid: a.asnid } }).then((r) => [a.asnid, r.data])
          )
        );
        setAssignmentSections(Object.fromEntries(entries));
      });
    }
    loadPlans();
  }, [year, typecls, canWritePlan, writingAsForeign, onBehalfTeachid, loadPlans]);

  // 학년/수업구분이 바뀌면 courseplan에 등록된 일일 수업시간을 기본값으로 채워준다 (직접 수정 가능)
  // 단, 기존 교수계획서를 수정하는 중에는 저장된 값을 덮어쓰지 않는다.
  useEffect(() => {
    if (!canWritePlan || !typecls || editingCrsuid) return;
    if (typecls === '잠정') {
      setDaytime(4);
      return;
    }
    api.get('/course-plan-daytime', { params: { grade, typecls } }).then((res) => {
      setDaytime(res.data.clstime ?? '');
    });
  }, [canWritePlan, grade, typecls, editingCrsuid]);

  const allSections = useMemo(
    () => Object.values(assignmentSections).flat(),
    [assignmentSections]
  );

  const checkedSections = useMemo(
    () => allSections.filter((s) => sectionState[s.crssectno]?.checked),
    [allSections, sectionState]
  );

  const totalHours = useMemo(
    () =>
      checkedSections.reduce(
        (acc, s) => {
          const state = sectionState[s.crssectno] || {};
          acc.thrytime += Number(state.thrytime) || 0;
          acc.practime += Number(state.practime) || 0;
          return acc;
        },
        { thrytime: 0, practime: 0 }
      ),
    [checkedSections, sectionState]
  );

  const involvedAssignments = useMemo(() => {
    const checkedIds = new Set(checkedSections.map((s) => s.crssectno));
    return assignments.filter((a) =>
      (assignmentSections[a.asnid] || []).some((s) => checkedIds.has(s.crssectno))
    );
  }, [assignments, assignmentSections, checkedSections]);

  const chapterLabel = useMemo(() => {
    if (involvedAssignments.length === 0) return '';
    return `[${involvedAssignments[0].subject}] ${involvedAssignments.map((a) => a.chapter).join(' / ')}`;
  }, [involvedAssignments]);

  useEffect(() => {
    if (editingCrsuid) return;
    const maxUnit = plans.reduce((m, p) => Math.max(m, p.unitno || 0), 0);
    setUnitno(maxUnit + 1);
  }, [plans, editingCrsuid]);

  const toggleSection = (crssectno) => {
    setSectionState((prev) => ({
      ...prev,
      [crssectno]: {
        checked: !prev[crssectno]?.checked,
        thrytime: prev[crssectno]?.thrytime ?? 0,
        practime: prev[crssectno]?.practime ?? 0,
        remark: prev[crssectno]?.remark || '',
      },
    }));
  };

  const setSectionHour = (crssectno, field, value) => {
    setSectionState((prev) => ({
      ...prev,
      [crssectno]: { ...prev[crssectno], [field]: value },
    }));
  };

  const setSectionRemark = (crssectno, value) => {
    setSectionState((prev) => ({
      ...prev,
      [crssectno]: { ...prev[crssectno], remark: value },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (checkedSections.length === 0) {
      setMessage('챕터선정에서 세부교육 내용을 하나 이상 선택해 주세요.');
      return;
    }
    if (totalHours.thrytime === 0 && totalHours.practime === 0) {
      setMessage('선택한 세부교육 내용에 학과 또는 실습 시간을 입력해 주세요.');
      return;
    }

    const chosenSections = checkedSections.map((s) => ({
      crssectno: s.crssectno,
      thrytime: Number(sectionState[s.crssectno]?.thrytime) || 0,
      practime: Number(sectionState[s.crssectno]?.practime) || 0,
      remark: sectionState[s.crssectno]?.remark || null,
    }));

    setLoading(true);
    setMessage('');
    try {
      if (editingCrsuid) {
        await api.put(`/teacher/lesson-plans/${editingCrsuid}`, {
          crsyear: year,
          grade,
          amtcode: involvedAssignments[0].amtcode,
          unitno,
          typecls,
          daytime: Number(daytime) || null,
          remark: remark || null,
          sections: chosenSections,
        });
        setMessage('교수계획서가 수정되었습니다.');
        setEditingCrsuid(null);
      } else {
        await api.post('/teacher/lesson-plans', {
          crsyear: year,
          grade,
          amtcode: involvedAssignments[0].amtcode,
          unitno,
          typecls,
          daytime: Number(daytime) || null,
          remark: remark || null,
          sections: chosenSections,
          teachid: writingAsForeign ? onBehalfTeachid : undefined,
        });
        setMessage('교수계획서가 저장되었습니다.');
      }
      setRemark('');
      setSectionState({});
      await loadPlans();
    } catch (err) {
      setMessage(err.response?.data?.detail || '저장에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (p) => {
    if (isAdminStaff) {
      setOnBehalfTeachid(p.techid !== user.teachid ? p.techid : '');
    }
    setTypecls(p.typecls || '정규');
    setGrade(p.grade);
    setUnitno(p.unitno);
    setDaytime(p.daytime ?? '');
    setRemark(p.remark || '');
    const nextSectionState = {};
    p.sections.forEach((s) => {
      nextSectionState[s.crssectno] = {
        checked: true,
        thrytime: s.thrytime,
        practime: s.practime,
        remark: s.remark || '',
      };
    });
    setSectionState(nextSectionState);
    setEditingCrsuid(p.crsuid);
    setMessage('');
  };

  const handleCancelEdit = () => {
    setEditingCrsuid(null);
    setSectionState({});
    setRemark('');
    setMessage('');
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`${p.unitno}차시 교수계획서를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/teacher/lesson-plans/${p.crsuid}`);
      if (editingCrsuid === p.crsuid) {
        handleCancelEdit();
      }
      await loadPlans();
    } catch (err) {
      alert(err.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const teacherNameOptions = useMemo(() => {
    const names = new Set(plans.map((p) => p.teacher_name).filter(Boolean));
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'ko'));
  }, [plans]);

  const filteredPlans = useMemo(() => {
    if (isTeacher) return plans;
    const nameQuery = teacherNameFilter.trim();
    const unitQuery = unitnoFilter.trim();
    return plans.filter((p) => {
      if (nameQuery && p.teacher_name !== nameQuery) return false;
      if (unitQuery && String(p.unitno) !== unitQuery) return false;
      return true;
    });
  }, [plans, isTeacher, teacherNameFilter, unitnoFilter]);

  return (
    <div className="page">
      <div className="page-title-row">
        <label>연도</label>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <h2>학년도&nbsp;&nbsp;교수계획서</h2>
        {isAdminStaff && (
          <div className="page-title-row-right">
            <label>원외교사</label>
            <select value={onBehalfTeachid} onChange={(e) => setOnBehalfTeachid(e.target.value)}>
              <option value="">선택 안 함</option>
              {foreignTeachers.map((t) => (
                <option key={t.teachid} value={t.teachid}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {canWritePlan && (
        <div className="card">
          <h3>차시별 교수계획서 작성</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-field">
                <label>배정 챕터</label>
                <div className="chapter-select-row">
                  <input type="text" readOnly value={chapterLabel} placeholder="챕터선정에서 선택하세요" />
                </div>
              </div>
              <div className="form-field form-field-grade">
                <label>학년</label>
                <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
                  <option value={1}>1학년</option>
                  <option value={2}>2학년</option>
                  <option value={3}>3학년</option>
                </select>
              </div>
              <div className="form-field form-field-unitno">
                <label>차수</label>
                <input type="number" min="1" value={unitno} onChange={(e) => setUnitno(Number(e.target.value))} />
              </div>
              <div className="form-field form-field-typecls">
                <label>수업구분</label>
                <div className="chapter-select-row">
                  <select value={typecls} onChange={(e) => setTypecls(e.target.value)}>
                    {typeclsOptions.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-field form-field-action">
                <label>일일 수업시간</label>
                <div className="chapter-select-row">
                  <input
                    type="number"
                    min="0"
                    className="daytime-input"
                    value={daytime}
                    onChange={(e) => setDaytime(e.target.value)}
                  />
                  <button type="button" className="btn-primary btn-inline" onClick={() => setPickerOpen(true)}>
                    챕터선정
                  </button>
                </div>
              </div>
            </div>

            <div className="hour-row">
              <div className="hour-field">
                <label>합계 (선택한 세부교육 내용 기준)</label>
                <div className="chapter-hour-total-hint">
                  학과 {totalHours.thrytime}시간 / 실습 {totalHours.practime}시간
                </div>
              </div>
              <div className="form-field">
                <label>비고</label>
                <input type="text" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="선택 입력" />
              </div>
            </div>

            {checkedSections.length > 0 && (
              <div className="section-list">
                <label className="field-label">세부교육 내용 (표준교재 섹션) - 학과/실습 시간 입력</label>
                {checkedSections.map((s) => (
                  <SelectedSectionRow
                    key={s.crssectno}
                    section={s}
                    state={sectionState[s.crssectno] || {}}
                    onHourChange={setSectionHour}
                    onRemarkChange={setSectionRemark}
                  />
                ))}
              </div>
            )}

            {message && <div className="form-message">{message}</div>}

            {editingCrsuid ? (
              <div className="lesson-plan-edit-actions">
                <button type="button" className="btn-ghost" onClick={handleCancelEdit}>취소</button>
                <button className="btn-primary btn-inline" type="submit" disabled={loading}>
                  {loading ? '수정 저장 중...' : '수정 저장'}
                </button>
              </div>
            ) : (
              <button className="btn-primary" type="submit" disabled={loading}>
                {loading ? '저장 중...' : '교수계획서 저장'}
              </button>
            )}
          </form>
        </div>
      )}

      <div className="card">
        <h3>{isTeacher ? '내 교수계획서 목록' : '전체 교수계획서 목록'} ({year}년)</h3>
        {!isTeacher && (
          <div className="form-row">
            <div className="form-field form-field-narrow">
              <label>교사이름</label>
              <select
                value={teacherNameFilter}
                onChange={(e) => setTeacherNameFilter(e.target.value)}
              >
                <option value="">전체</option>
                {teacherNameOptions.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>차시</label>
              <input
                type="number"
                min="1"
                value={unitnoFilter}
                onChange={(e) => setUnitnoFilter(e.target.value)}
                placeholder="전체"
              />
            </div>
          </div>
        )}
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>학년</th>
                <th>과목</th>
                <th>교육내용</th>
                <th>담당교사</th>
                <th>차수</th>
                <th>학과</th>
                <th>실습</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.map((p) => (
                <tr key={p.crsuid} className="board-row" onClick={() => openLessonPlanPrint(p)}>
                  <td>{p.grade}</td>
                  <td>{p.subject}</td>
                  <td>{p.chapter}</td>
                  <td>{p.teacher_name}</td>
                  <td>{p.unitno}</td>
                  <td>{p.thrytime}</td>
                  <td>{p.practime}</td>
                  <td className="board-title-cell" onClick={(e) => e.stopPropagation()}>
                    {p.can_edit && <button className="btn-link" onClick={() => handleEdit(p)}>수정</button>}
                    {p.can_edit && p.can_delete && ' '}
                    {p.can_delete && <button className="btn-link" onClick={() => handleDelete(p)}>삭제</button>}
                  </td>
                </tr>
              ))}
              {filteredPlans.length === 0 && (
                <tr>
                  <td colSpan="8" className="empty-row">등록된 교수계획서가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pickerOpen && (
        <ChapterPickerModal
          assignments={assignments}
          assignmentSections={assignmentSections}
          sectionState={sectionState}
          toggleSection={toggleSection}
          onHourChange={setSectionHour}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

function SelectedSectionRow({ section, state, onHourChange, onRemarkChange }) {
  return (
    <div className="section-item">
      <div className="section-item-row">
        <div className="section-check">{section.sections}</div>
        <div className="section-hour-group">
          <div className="chapter-hour-input-wrap">
            학과
            <input
              type="number"
              min="0"
              value={state.thrytime ?? 0}
              onChange={(e) => onHourChange(section.crssectno, 'thrytime', e.target.value)}
            />
          </div>
          <div className="chapter-hour-input-wrap">
            실습
            <input
              type="number"
              min="0"
              value={state.practime ?? 0}
              onChange={(e) => onHourChange(section.crssectno, 'practime', e.target.value)}
            />
          </div>
        </div>
      </div>
      <textarea
        className="section-remark"
        rows={1}
        placeholder="세부 서술 (예: 가) 무게와 힘  나) 압력  다) 대기압)"
        value={state.remark || ''}
        onChange={(e) => onRemarkChange(section.crssectno, e.target.value)}
      />
    </div>
  );
}
